import React from "react";

import { formatNumber, useTranslation } from "utils";

import { NO_VALUE } from "consts";

import { SoilSightFieldSoil } from "models";

import { Typography } from "components";

import { SectionHeading } from "pages/app/scenario-analysis/components/SectionHeading";

export interface FieldSummarySectionProps {
  /** Year-zero soil readings for the field; null until a run has reported them. */
  fieldSoil: SoilSightFieldSoil | null;
  onExplainFieldSoil: () => void;
}

/* Percentages and decimals go through Intl like every other figure on the page, so the separator
   follows the locale — German wants "1,2%", not "1.2%". */

/** Texture shares read as whole percent — a tenth of a percent of sand is below what the source justifies. */
const formatTextureShare = (value: number | null): string => (value === null ? NO_VALUE : `${formatNumber({ number: value, decimalPlaces: 0 })}%`);

/** Organic carbon sits in the low single digits, so a whole percent would throw most of it away. */
const formatCarbon = (value: number | null): string => (value === null ? NO_VALUE : `${formatNumber({ number: value, decimalPlaces: 1 })}%`);

/** pH is read to one decimal, the precision the scale is meaningful at. */
const formatPh = (value: number | null): string => (value === null ? NO_VALUE : formatNumber({ number: value, decimalPlaces: 1 }));

/** Full-width section under economics; the heading sits beside the readings so they take the row's width. */
export const FieldSummarySection = ({ fieldSoil, onExplainFieldSoil }: FieldSummarySectionProps) => {
  const { t } = useTranslation("scenarioAnalysis");

  const composition = [
    { key: "sand", value: fieldSoil?.sand ?? null },
    { key: "silt", value: fieldSoil?.silt ?? null },
    { key: "clay", value: fieldSoil?.clay ?? null },
  ];

  return (
    <section className="field-summary-section" data-testid="field-summary-section">
      <SectionHeading
        title={t("aboutYourField.title")}
        supporting={t("aboutYourField.source")}
        infoLabel={t("aboutYourField.infoLabel")}
        onInfo={onExplainFieldSoil}
        dataTestId="field-summary-section-heading"
      />

      <div className="field-summary-readings">
        <div className="field-summary-reading" data-testid="field-summary-composition">
          <Typography variant="paragraph-m" className="field-summary-reading-label">
            {t("aboutYourField.composition")}
          </Typography>
          <Typography variant="paragraph-m" className="field-summary-reading-value">
            {composition.map(({ key, value }) => t(`aboutYourField.${key}`, { value: formatTextureShare(value) })).join(" · ")}
          </Typography>
        </div>

        <div className="field-summary-reading" data-testid="field-summary-ph">
          <Typography variant="paragraph-m" className="field-summary-reading-label">
            {t("aboutYourField.ph")}
          </Typography>
          <Typography variant="paragraph-m" className="field-summary-reading-value">
            {formatPh(fieldSoil?.ph ?? null)}
          </Typography>
        </div>

        <div className="field-summary-reading" data-testid="field-summary-carbon">
          <Typography variant="paragraph-m" className="field-summary-reading-label">
            {t("aboutYourField.organicCarbon")}
          </Typography>
          <Typography variant="paragraph-m" className="field-summary-reading-value">
            {formatCarbon(fieldSoil?.carbon ?? null)}
          </Typography>
        </div>
      </div>
    </section>
  );
};

export default FieldSummarySection;
